import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, MusicalNoteIcon, FaceSmileIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import AudioUpload from '../AudioUpload';

interface EmotionSelectorProps {
  emoText: string;
  emoAudioUrl?: string;
  onEmoTextChange: (text: string) => void;
  onEmoAudioChange?: (url: string) => void;
  disabled?: boolean;
  episodeId?: string;
  showAudioUpload?: boolean;
}

export default function EmotionSelector({
  emoText,
  emoAudioUrl = '',
  onEmoTextChange,
  onEmoAudioChange,
  disabled = false,
  episodeId,
  showAudioUpload = true
}: EmotionSelectorProps) {
  const [showPanel, setShowPanel] = useState(false);
  const [showAudioUploadPanel, setShowAudioUploadPanel] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation('common_ui');

  // 从翻译文件加载当前语言的预设情绪列表
  const emotionPresets = t('emotionSelector.presets', { returnObjects: true }) as string[];

  // 进入编辑模式时聚焦
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // 选择预设情绪
  const handleSelectPreset = (preset: string) => {
    onEmoTextChange(preset);
    setShowPanel(false);
  };

  // 开始编辑
  const handleStartEdit = () => {
    if (disabled) return;
    setEditText(emoText);
    setIsEditing(true);
  };

  // 完成编辑
  const handleFinishEdit = () => {
    if (editText.trim()) {
      onEmoTextChange(editText.trim());
    }
    setIsEditing(false);
  };

  // 清除情绪
  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onEmoTextChange('');
    setIsEditing(false);
  };

  return (
    <div className="flex items-center gap-1">
      {/* 情绪按钮/编辑框 */}
      <div className="relative">
        {isEditing ? (
          // 编辑模式
          <div className="flex items-center gap-1 bg-purple-600/20 rounded-lg px-2 py-1.5 border border-purple-500/30">
            <input
              ref={inputRef}
              type="text"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onBlur={handleFinishEdit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleFinishEdit();
                if (e.key === 'Escape') setIsEditing(false);
              }}
              className="bg-transparent text-purple-400 text-xs outline-none w-20"
              placeholder={t('emotionSelector.placeholder')}
            />
            <button
              type="button"
              onMouseDown={handleClear}
              className="text-purple-400 hover:text-red-400 transition-colors"
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          </div>
        ) : emoText ? (
          // 已选择情绪
          <div className="flex items-center gap-1 bg-purple-600/20 rounded-lg px-2 py-1.5 border border-purple-500/30">
            <span
              className="text-purple-400 text-xs max-w-[80px] truncate cursor-pointer hover:text-purple-300"
              onClick={handleStartEdit}
              title={t('emotionSelector.clickToEdit')}
            >
              {emoText}
            </span>
            <button
              type="button"
              onClick={() => !disabled && setShowPanel(true)}
              disabled={disabled}
              className="text-purple-400 hover:text-purple-300 transition-colors"
              title={t('emotionSelector.selectOther')}
            >
              <ChevronDownIcon className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={handleClear}
              disabled={disabled}
              className="text-purple-400 hover:text-red-400 transition-colors"
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          </div>
        ) : (
          // 未选择情绪
          <button
            type="button"
            onClick={() => !disabled && setShowPanel(true)}
            disabled={disabled}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded-lg border transition-colors disabled:opacity-50 bg-bg-elevated hover:bg-border-default text-gray-200 border-border-default"
          >
            <FaceSmileIcon className="h-4 w-4 text-gray-400" />
            <span>{t('emotionSelector.label')}</span>
          </button>
        )}

        {/* 情绪选择面板 */}
        {showPanel && (
          <div className="absolute bottom-full left-0 mb-2 bg-bg-surface border border-border-subtle rounded-lg shadow-lg z-50 p-3 w-[320px]">
            {/* 标题栏 */}
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm text-gray-300">{t('emotionSelector.selectTitle')}</span>
              <button
                type="button"
                onClick={() => setShowPanel(false)}
                className="text-gray-500 hover:text-gray-300 transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            {/* 预设情绪标签 */}
            <div className="flex flex-wrap gap-1.5">
              {emotionPresets.map(preset => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => handleSelectPreset(preset)}
                  className={`px-2 py-1 text-xs rounded-md transition-colors ${emoText === preset
                    ? 'bg-purple-600 text-white'
                    : 'bg-bg-elevated hover:bg-border-default text-gray-300 border border-border-default'
                    }`}
                >
                  {preset}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 音频情感按钮 */}
      {showAudioUpload && onEmoAudioChange && (
        <div className="relative">
          {emoAudioUrl ? (
            <div className="flex items-center gap-1 bg-purple-600/20 rounded-lg px-2 py-1.5 border border-purple-500/30">
              <MusicalNoteIcon className="h-4 w-4 text-purple-400" />
              <button
                type="button"
                onClick={() => onEmoAudioChange('')}
                disabled={disabled}
                className="text-purple-400 hover:text-red-400 transition-colors"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => !disabled && setShowAudioUploadPanel(!showAudioUploadPanel)}
              disabled={disabled}
              className="p-1.5 rounded-lg transition-colors disabled:opacity-50 bg-bg-elevated text-gray-400 hover:bg-border-default border border-border-default"
              title={t('emotionSelector.uploadEmoAudio')}
            >
              <MusicalNoteIcon className="h-4 w-4" />
            </button>
          )}

          {/* 音频上传面板 */}
          {showAudioUploadPanel && (
            <div className="absolute bottom-full left-0 mb-2 bg-bg-surface border border-border-subtle rounded-lg shadow-lg z-50 p-3 min-w-[280px]">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs text-gray-400">{t('emotionSelector.uploadEmoAudio')}</span>
                <button
                  type="button"
                  onClick={() => setShowAudioUploadPanel(false)}
                  className="text-gray-500 hover:text-gray-300"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
              <AudioUpload
                episodeId={episodeId}
                value=""
                onUploadSuccess={(url) => {
                  onEmoAudioChange(url);
                  setShowAudioUploadPanel(false);
                }}
                placeholder={t('emotionSelector.uploadEmoAudio')}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
